import type { CurvaDocument } from "./document";
import type { SceneNode } from "./nodes";
import {
  normalizeStrokeStack,
  type NodeEffect,
  type NodeStyle,
  type Paint,
  type StrokeStyle,
} from "./styles";
import {
  resolvedTextStyleAt,
  textWithAppliedTypography,
  type RichTextModel,
  type TextInspectorRange,
  type TextTypography,
  type TypeStyle,
} from "./text";
import { createId } from "@/lib/ids";

export type DocumentStyleKind = "fill" | "stroke" | "text" | "effects";

export interface StyleBindings {
  fill?: string;
  stroke?: string;
  text?: string;
  effects?: string;
}

export interface DocumentStyleBase {
  id: string;
  name: string;
  kind: DocumentStyleKind;
}

export interface FillDocumentStyle extends DocumentStyleBase {
  kind: "fill";
  fills: Paint[];
}

export interface StrokeDocumentStyle extends DocumentStyleBase {
  kind: "stroke";
  strokes: StrokeStyle[];
}

export type { TextTypography } from "./text";

export interface TextDocumentStyle extends DocumentStyleBase {
  kind: "text";
  typography: TextTypography;
}

export interface EffectsDocumentStyle extends DocumentStyleBase {
  kind: "effects";
  effects: NodeEffect[];
}

export type DocumentStyle =
  | FillDocumentStyle
  | StrokeDocumentStyle
  | TextDocumentStyle
  | EffectsDocumentStyle;

export interface DocumentStyleLibrary {
  items: DocumentStyle[];
}

export function createEmptyStyleLibrary(): DocumentStyleLibrary {
  return { items: [] };
}

export function normalizeStyleLibrary(value: unknown): DocumentStyleLibrary {
  if (!value || typeof value !== "object") {
    return createEmptyStyleLibrary();
  }

  const record = value as Record<string, unknown>;
  if (Array.isArray(record.items)) {
    return { items: record.items as DocumentStyle[] };
  }

  return createEmptyStyleLibrary();
}

export function createDocumentStyleId(): string {
  return createId("style");
}

export function findDocumentStyle(document: CurvaDocument, styleId: string): DocumentStyle | undefined {
  return normalizeStyleLibrary(document.styles).items.find(style => style.id === styleId);
}

export function listDocumentStylesByKind(document: CurvaDocument, kind: DocumentStyleKind): DocumentStyle[] {
  return normalizeStyleLibrary(document.styles).items.filter(style => style.kind === kind);
}

export function documentStyleUsageCount(nodes: SceneNode[], styleId: string): number {
  return nodes.filter(node => Object.values(node.styleBindings ?? {}).includes(styleId)).length;
}

export function styleKindForBinding(binding: keyof StyleBindings): DocumentStyleKind {
  return binding;
}

export function bindingKeyForStyleKind(kind: DocumentStyleKind): keyof StyleBindings {
  return kind;
}

function cloneBindings(bindings: StyleBindings | undefined): StyleBindings | undefined {
  return bindings ? structuredClone(bindings) : undefined;
}

export function nodeStyleBindings(node: SceneNode): StyleBindings | undefined {
  return node.styleBindings;
}

export function styleBindingId(node: SceneNode, kind: DocumentStyleKind): string | undefined {
  return node.styleBindings?.[bindingKeyForStyleKind(kind)];
}

export function resolveFillPaints(document: CurvaDocument, node: SceneNode): Paint[] {
  const styleId = styleBindingId(node, "fill");
  if (styleId) {
    const style = findDocumentStyle(document, styleId);
    if (style?.kind === "fill") {
      return structuredClone(style.fills);
    }
  }
  return structuredClone(node.style.fills);
}

export function resolveStrokeStyles(document: CurvaDocument, node: SceneNode): StrokeStyle[] {
  const styleId = styleBindingId(node, "stroke");
  if (styleId) {
    const style = findDocumentStyle(document, styleId);
    if (style?.kind === "stroke") {
      return structuredClone(style.strokes);
    }
  }
  return structuredClone(normalizeStrokeStack(node.style));
}

export function resolveEffects(document: CurvaDocument, node: SceneNode): NodeEffect[] {
  const styleId = styleBindingId(node, "effects");
  if (styleId) {
    const style = findDocumentStyle(document, styleId);
    if (style?.kind === "effects") {
      return structuredClone(style.effects);
    }
  }
  return structuredClone(node.style.effects ?? []);
}

export function resolveTextTypography(document: CurvaDocument, node: SceneNode): TextTypography | undefined {
  if (node.type !== "text") {
    return undefined;
  }

  const styleId = styleBindingId(node, "text");
  if (!styleId) {
    return undefined;
  }

  const style = findDocumentStyle(document, styleId);
  if (style?.kind !== "text") {
    return undefined;
  }

  return structuredClone(style.typography);
}

export function resolveTextFillPaint(document: CurvaDocument, node: SceneNode): Paint | undefined {
  if (node.type !== "text") {
    return undefined;
  }

  const styleId = styleBindingId(node, "fill");
  if (styleId) {
    const style = findDocumentStyle(document, styleId);
    if (style?.kind === "fill") {
      return structuredClone(style.fills.at(-1));
    }
  }

  return node.text.style.fill ? structuredClone(node.text.style.fill) : node.style.fills.at(-1)
    ? structuredClone(node.style.fills.at(-1)!)
    : undefined;
}

export function getEffectiveNodeStyle(document: CurvaDocument, node: SceneNode): NodeStyle {
  return {
    ...node.style,
    fills: resolveFillPaints(document, node),
    strokes: resolveStrokeStyles(document, node),
    stroke: resolveStrokeStyles(document, node)[0],
    effects: resolveEffects(document, node),
  };
}

export function getEffectiveTextModel(document: CurvaDocument, node: SceneNode): RichTextModel | undefined {
  if (node.type !== "text") {
    return undefined;
  }

  const typography = resolveTextTypography(document, node);
  const fill = resolveTextFillPaint(document, node);

  if (!typography) {
    if (fill && JSON.stringify(fill) !== JSON.stringify(node.text.style.fill)) {
      return {
        ...node.text,
        style: {
          ...node.text.style,
          fill,
        },
      };
    }
    return node.text;
  }

  return textWithAppliedTypography(node.text, typography, fill);
}

export function applyStyleBinding(node: SceneNode, style: DocumentStyle): SceneNode {
  const key = bindingKeyForStyleKind(style.kind);
  return {
    ...node,
    styleBindings: {
      ...node.styleBindings,
      [key]: style.id,
    },
  };
}

export function detachStyleBinding(node: SceneNode, kind: DocumentStyleKind): SceneNode {
  if (!node.styleBindings?.[bindingKeyForStyleKind(kind)]) {
    return node;
  }

  const nextBindings = { ...node.styleBindings };
  delete nextBindings[bindingKeyForStyleKind(kind)];
  return {
    ...node,
    styleBindings: Object.keys(nextBindings).length > 0 ? nextBindings : undefined,
  };
}

export function inlineStyleBinding(
  document: CurvaDocument,
  node: SceneNode,
  kind: DocumentStyleKind,
): SceneNode {
  let next = detachStyleBinding(node, kind);

  switch (kind) {
    case "fill":
      if (next.type === "text") {
        const fill = resolveTextFillPaint(document, node);
        next = {
          ...next,
          text: {
            ...next.text,
            style: {
              ...next.text.style,
              fill,
            },
          },
        };
      } else {
        next = {
          ...next,
          style: {
            ...next.style,
            fills: resolveFillPaints(document, node),
          },
        };
      }
      break;
    case "stroke":
      next = {
        ...next,
        style: {
          ...next.style,
          strokes: resolveStrokeStyles(document, node),
          stroke: resolveStrokeStyles(document, node)[0],
        },
      };
      break;
    case "effects":
      next = {
        ...next,
        style: {
          ...next.style,
          effects: resolveEffects(document, node),
        },
      };
      break;
    case "text":
      if (next.type === "text") {
        const typography = resolveTextTypography(document, node);
        if (typography) {
          next = {
            ...next,
            text: textWithAppliedTypography(next.text, typography, next.text.style.fill),
          };
        }
      }
      break;
  }

  return next;
}

export function snapshotTypographyFromText(text: RichTextModel, range: TextInspectorRange): TextTypography {
  const index = range.start < text.characters.length ? range.start : Math.max(0, text.characters.length - 1);
  const style = resolvedTextStyleAt(text, index);
  const { fill: _fill, ...typography } = style;
  return structuredClone(typography);
}

export interface CreateStyleFromSelectionOptions {
  textRange?: TextInspectorRange;
  fromTextFill?: boolean;
}

export function createStyleFromSelection(
  node: SceneNode,
  kind: DocumentStyleKind,
  name: string,
  options: CreateStyleFromSelectionOptions = {},
): DocumentStyle {
  const id = createDocumentStyleId();
  const trimmedName = name.trim() || "Style";

  switch (kind) {
    case "fill": {
      if (node.type === "text" || options.fromTextFill) {
        const fill = node.type === "text" ? node.text.style.fill : undefined;
        return {
          id,
          name: trimmedName,
          kind: "fill",
          fills: fill ? [structuredClone(fill)] : structuredClone(node.style.fills),
        };
      }
      return {
        id,
        name: trimmedName,
        kind: "fill",
        fills: structuredClone(node.style.fills),
      };
    }
    case "stroke":
      return {
        id,
        name: trimmedName,
        kind: "stroke",
        strokes: structuredClone(normalizeStrokeStack(node.style)),
      };
    case "effects":
      return {
        id,
        name: trimmedName,
        kind: "effects",
        effects: structuredClone(node.style.effects ?? []),
      };
    case "text":
      if (node.type !== "text") {
        throw new Error("Text styles can only be created from text nodes.");
      }
      return {
        id,
        name: trimmedName,
        kind: "text",
        typography: snapshotTypographyFromText(node.text, options.textRange ?? { start: 0, end: node.text.characters.length }),
      };
  }
}

export function cloneStyleBindings(bindings: StyleBindings | undefined): StyleBindings | undefined {
  return cloneBindings(bindings);
}
